import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/d71nf18hb0rqicn68ko0";

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function ContactSection() {
  const [status, setStatus] = useState<FormStatus>("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    const form = e.currentTarget;
    const data = new URLSearchParams();

    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="py-28 bg-stone-50 px-8 lg:px-20">
      <div className="max-w-screen-xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
          {/* Left Info */}
          <div>
            <p className="text-xs tracking-[0.35em] text-stone-400 mb-4"
               style={{ fontFamily: "'DM Sans', sans-serif" }}>
              GET IN TOUCH · 联系我们
            </p>
            <h2
              className="text-6xl font-black text-stone-900 leading-tight mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              LET'S BUILD<br />TOGETHER
            </h2>
            <p className="text-sm text-stone-500 leading-relaxed mb-2 max-w-sm"
               style={{ fontFamily: "'DM Sans', sans-serif" }}>
              无论是商业零售、文化空间还是住宅，我们期待听到您的想法，共同探索设计的无限可能。
            </p>
            <p className="text-xs text-stone-400 leading-relaxed italic mb-12 max-w-sm"
               style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Whether retail, cultural space, or residential — we look forward to hearing your ideas and exploring the infinite possibilities of design together.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-stone-200 shrink-0">
                  <i className="ri-map-pin-line text-stone-500" />
                </div>
                <div>
                  <p className="text-xs tracking-widest text-stone-400 mb-1"
                     style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    STUDIO · 工作室地址
                  </p>
                  <p className="text-sm text-stone-700"
                     style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    上海市嘉定区栖林路425弄7号<br /><br />
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-stone-200 shrink-0">
                  <i className="ri-mail-line text-stone-500" />
                </div>
                <div>
                  <p className="text-xs tracking-widest text-stone-400 mb-1"
                     style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    EMAIL · 邮箱
                  </p>
                  <p className="text-sm text-stone-700"
                     style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    wextin@foxmail.com
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-stone-200 shrink-0">
                  <i className="ri-phone-line text-stone-500" />
                </div>
                <div>
                  <p className="text-xs tracking-widest text-stone-400 mb-1"
                     style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    PHONE · 电话
                  </p>
                  <p className="text-sm text-stone-700"
                     style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    +86 189 1826 8808
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white p-10">
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-50 mb-6">
                  <i className="ri-check-line text-2xl text-amber-700" />
                </div>
                <h3 className="text-2xl font-bold text-stone-900 mb-3"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Message Sent · 消息已发送
                </h3>
                <p className="text-sm text-stone-500"
                   style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  感谢您的留言，我们将在 24 小时内与您联系。<br />
                  <span className="text-xs text-stone-400 italic">Thank you for your message. We will get back to you within 24 hours.</span>
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-8 text-xs tracking-widest text-stone-500 hover:text-stone-900 transition-colors cursor-pointer underline underline-offset-4"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  SEND ANOTHER MESSAGE · 再次留言
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs tracking-widest text-stone-400 mb-2"
                           style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      NAME · 姓名 *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="您的姓名 / Your name"
                      className="w-full bg-stone-50 border-0 border-b border-stone-200 px-0 py-3 text-sm text-stone-800 placeholder-stone-400 outline-none focus:border-stone-900 transition-colors duration-200"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs tracking-widest text-stone-400 mb-2"
                           style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      EMAIL · 邮箱 *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full bg-stone-50 border-0 border-b border-stone-200 px-0 py-3 text-sm text-stone-800 placeholder-stone-400 outline-none focus:border-stone-900 transition-colors duration-200"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs tracking-widest text-stone-400 mb-2"
                           style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      PHONE · 电话
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+86 xxx xxxx xxxx"
                      className="w-full bg-stone-50 border-0 border-b border-stone-200 px-0 py-3 text-sm text-stone-800 placeholder-stone-400 outline-none focus:border-stone-900 transition-colors duration-200"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs tracking-widest text-stone-400 mb-2"
                           style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      PROJECT TYPE · 项目类型
                    </label>
                    <select
                      name="project_type"
                      className="w-full bg-stone-50 border-0 border-b border-stone-200 px-0 py-3 text-sm text-stone-800 outline-none focus:border-stone-900 transition-colors duration-200 cursor-pointer"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      <option value="">Select type · 请选择</option>
                      <option value="Residential">住宅 Residential</option>
                      <option value="Commercial">商业 Commercial</option>
                      <option value="Cultural">文化 Cultural</option>
                      <option value="Hospitality">酒店 Hospitality</option>
                      <option value="Retail">零售 Retail</option>
                      <option value="Brand Space">品牌空间 Brand Space</option>
                      <option value="Other">其他 Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs tracking-widest text-stone-400 mb-2"
                         style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    MESSAGE · 留言 *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="请描述您的项目需求 / Tell us about your project..."
                    className="w-full bg-stone-50 border-0 border-b border-stone-200 px-0 py-3 text-sm text-stone-800 placeholder-stone-400 outline-none focus:border-stone-900 transition-colors duration-200 resize-none"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  />
                </div>

                {status === "error" && (
                  <p className="text-xs text-red-500"
                     style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    提交失败，请重试。<span className="italic text-red-400 ml-1">Submission failed. Please try again.</span>
                  </p>
                )}

                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="flex items-center gap-4 bg-stone-900 text-white px-10 py-4 text-xs tracking-[0.2em] hover:bg-amber-700 transition-colors duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {status === "submitting" ? "SENDING · 发送中..." : "SEND MESSAGE · 发送留言"}
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                    </div>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
